import { useMemo } from 'react'
import { ListItem } from '@mui/material'
import {
  ProjectDocumentRuleType,
  ProjectGridFieldRuleType,
} from '@/types/rules'
import { getFieldsFromProject, numberFieldTypes } from '@/utils/field'
import { FormAutocomplete } from '@/components/forms'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

type DependentFieldsAutocompleteProps = {
  ruleType: ProjectGridFieldRuleType | ProjectDocumentRuleType
  fieldId?: string
}

export default function DependentFieldsAutocomplete({
  ruleType,
  fieldId,
}: DependentFieldsAutocompleteProps) {
  const { project, fieldsMap } = useProjectContext()
  const isComparisonSumRule = ruleType.code === 'flag_comparison_sum'
  const isDateYearCalculateRule = ruleType.code === 'date_calculate_year'
  const isClearFieldRule =
    ruleType.code === 'clear_field_when_dependency_changes'
  const isFlagRowRule = ruleType.code.startsWith('flag_row_count')

  const currentField = useMemo(() => {
    if (!fieldId) return null
    return fieldsMap[fieldId]
  }, [fieldId, fieldsMap])

  const options = useMemo(() => {
    const allFields = getFieldsFromProject(project)

    return allFields
      .filter(({ id, project_grid_field_type }) => {
        const projectField = fieldsMap[id]

        if (isFlagRowRule) {
          return project_grid_field_type.code === 'grid'
        }

        if (project_grid_field_type.code === 'grid') return false

        if (isClearFieldRule) {
          if (currentField?.parentField) {
            return (
              id !== fieldId &&
              (!projectField.parentField ||
                projectField.parentField.id === currentField.parentField.id)
            )
          }

          return id !== fieldId && !projectField.parentField
        }

        return !(
          (isComparisonSumRule &&
            (id === fieldId ||
              !numberFieldTypes.includes(project_grid_field_type.code))) ||
          (isDateYearCalculateRule &&
            (id === fieldId ||
              !['datetime', 'date'].includes(project_grid_field_type.code)))
        )
      })
      .map(({ id, name }) => {
        const projectField = fieldsMap[id]
        const fullName = projectField.parentField
          ? `${projectField.parentField.name}: ${name}`
          : name
        return { id, full_name: fullName }
      })
  }, [
    project,
    isFlagRowRule,
    isClearFieldRule,
    isComparisonSumRule,
    fieldId,
    isDateYearCalculateRule,
    currentField,
    fieldsMap,
  ])

  return (
    <FormAutocomplete
      name="dependent_fields"
      label={ruleType.dependent_fields_label}
      helperText={ruleType.dependent_fields_description}
      options={options}
      multiple
      autoHighlight
      disableCloseOnSelect
      noOptionsText="No fields available."
      textFieldProps={{
        variant: 'outlined',
        InputLabelProps: { shrink: true },
      }}
      required={!!ruleType.dependent_fields_min}
      isOptionEqualToValue={(option, value) => option?.id === value.id}
      getOptionLabel={(option) => option?.full_name || ''}
      maxSelected={ruleType.dependent_fields_max || undefined}
      // This is so we can choose the key, otherwise it throws a duplicate key error when we have duplicates
      renderOption={(props, option) => {
        return (
          <ListItem {...props} key={option.id} dense>
            {option.full_name}
          </ListItem>
        )
      }}
    />
  )
}
